'use client';

import { c as _c } from "react/compiler-runtime";
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { PropsWithChildren, useRef, useState } from 'react';
import { collapseAnimation } from './Collapse.css';
import { overflowYClip } from '@/uiPrimitives/utility/overflow.css';
import { addTestSelector, testSelectorAttribute } from '@/globals/testHelpers/testSelector';
import { useMatchMedia } from '@/globals/helpers/useMatchMedia';
export const useAnimationSettled = () => {
  const $ = _c(4);
  const [animationSettled, setAnimationSettled] = useState(true);
  const prefersReduceMotion = useMatchMedia("(prefers-reduced-motion: reduce)");
  const t0 = prefersReduceMotion ? true : animationSettled;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setAnimationSettled(true);
    };
    t2 = () => {
      setAnimationSettled(false);
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t0) {
    t3 = {
      animationSettled: t0,
      onTransitionEnd: t1,
      onTransitionStart: t2
    };
    $[2] = t0;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export const useCollapse = isOpened => {
  const $ = _c(15);
  const outerRef = useRef(null);
  const {
    animationSettled,
    onTransitionEnd,
    onTransitionStart
  } = useAnimationSettled();
  const t0 = collapseAnimation[isOpened ? "opened" : "closed"];
  let t1;
  if ($[0] !== t0) {
    t1 = classes(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onTransitionEnd || $[3] !== onTransitionStart || $[4] !== t1) {
    t2 = {
      ref: outerRef,
      className: t1,
      onTransitionStart,
      onTransitionEnd
    };
    $[2] = onTransitionEnd;
    $[3] = onTransitionStart;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !(isOpened && animationSettled) && overflowYClip;
  let t4;
  if ($[6] !== t3) {
    t4 = classes(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isOpened && animationSettled;
  const t6 = !isOpened;
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      className: t4,
      "data-fully-opened": t5,
      inert: t6
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t7) {
    t8 = {
      outerProps: t2,
      innerProps: t7
    };
    $[12] = t2;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8 as const;
};
export const Collapse = ({
  isOpened,
  children,
  id,
  [testSelectorAttribute]: dataUITest
}: PropsWithChildren<{
  isOpened: boolean;
  id: string;
  [testSelectorAttribute]?: string;
}>) => {
  const {
    innerProps,
    outerProps
  } = useCollapse<HTMLSpanElement>(isOpened);
  return <span id={id} {...outerProps} {...addTestSelector(dataUITest)}>
			<span {...innerProps}>{children}</span>
		</span>;
};